import React, { useState, useCallback, useEffect } from "react";
import moment from "moment";
import JSZip from "jszip";
import { useRouter } from "next/router";
import { FaSpinner } from "react-icons/fa";
import { BsImageFill, BsFileEarmark, BsLink45Deg, BsChatSquareTextFill } from "react-icons/bs";
import { RiVideoFill } from "react-icons/ri";
import { BsTable } from "react-icons/bs";
import { toast } from "react-toastify";
import Masonry from "react-masonry-css";
import { saveAs } from "~/utils/window";
import DashboardHeader, { SubmenuItem } from "~/components/DashboardHeader";
import Spinner from "~/shared/Spinner";
import api from "~/services/api";
import mainApi from "~/adapters/api";
import useAuth from "~/hooks/useAuth";
import CircleButton from "../../Button/CircleButton";
import Lightbox from "./Lightbox";
import { FTOPDownload } from "../../../styles/icons";
import ShareGallery from "../ShareGallery";
import { getAddressFormatted } from "../../../utils/address";
import Container, { DownloadButton, MessageContent, StatusBox } from "./styles";
import DownloadDrawer from "./DownloadDrawer";
import Panel from "~/shared/Panel";
import ExecuteIntegrationModal from "./ExecuteIntegrationModal";
import Tippy from "@tippyjs/react";
import DataTable from "~/shared/DataTable";
import RequestCreationModal from "~/components/RequestCreationModal";
import "tippy.js/dist/tippy.css";
import Notification from "~/shared/NotificationRequestMessege";
import renderDialog from "~/components/Alert";
import Title from "~/shared/Title";
import Request from "~/services/request";
import { Badge } from "~/shared/Badge";
import Rating from "~/shared/Rating";
import FeedbackItem from "~/services/feedbackItem";
import EvaluationModal from "./EvaluationModal";
import FeedbackJob from "~/services/feedbackJob";

const findFilenameOnUrl = (url) => {
    if (!url) {
        return url;
    }

    const name = url?.split("/")?.pop()?.split("?")?.[0];
    return decodeURIComponent(name);
};

const otherFilesItemStyle = {
    borderBottom: "1px solid #dcdcdc",
    width: "100%",
    margin: "10px 0",
    paddingBottom: "10px",
};

const downloadProgressInitialState = {
    imagesTotal: 0,
    imagesDownloaded: 0,
    videosTotal: 0,
    videosDownloaded: 0,
    othersTotal: 0,
    othersDownloaded: 0,
};

const Status = ({ row }) => {
    const currentLetter = row.status === "aguardando" ? "A" : "R";
    return (
        <Tippy content={row.status}>
            <StatusBox status={row.status}>
                <p>{currentLetter}</p>
            </StatusBox>
        </Tippy>
    );
};

const Gallery = ({ job, photos, cover, token = null, hideControls = false }) => {
    const router = useRouter();
    const { account } = useAuth();
    const [showLightbox, setShowLightbox] = useState(false);
    const [currentPhotoId, setCurrentPhotoId] = useState();
    const [loadingLink, setLoadingLink] = useState([]);
    const [preparingDownload, setPreparingDownload] = useState(false);
    const [showIntegrationModal, setShowIntegrationModal] = useState(false);
    const [requestModalOpen, setRequestModalOpen] = useState(false);
    const [requests, setRequests] = useState([]);
    const [loadRequest, setLoadRequest] = useState(false);
    const [feedbackItens, setFeedbackItens] = useState([]);
    const [feedbackJobList, setFeedbackJobList] = useState([]);
    const [openFeedbackModal, setOpenFeedBackModal] = useState(false);

    const download = (fileId) => {
        setLoadingLink((a) => [...a, fileId]);
        const withToken = token ? `/${token}` : "";
        api.get(`/v2/customer/jobs/${job.id}/files/${fileId}/download-link${withToken}`)
            .then((res) => res.data.record)
            .then((downloadUrl) => {
                window.location.href = downloadUrl;
            })
            .catch((e) => {
                toast.error(`Erro ao fazer o download, ${e}`);
                console.log(e);
            })
            .finally(() => setLoadingLink((a) => a.filter((i) => i !== fileId)));
    };

    const getRequest = useCallback(async (jobId, organizationId) => {
        setLoadRequest(true);
        return Request.list({ filters: { jobId, organizationId } })
            .then((res) => setRequests(res.records))
            .catch((err) => {
                toast.error(`Não foi recuperar os dados da solicitação: ${err?.response?.data?.msg || err.message}`);
            })
            .finally(() => setLoadRequest(false));
    }, []);

    const lightbox = (id) => {
        setCurrentPhotoId(id);
        setShowLightbox(true);
    };

    const { photoFiles, videoFiles, otherFiles } = photos.reduce(
        (acc, item) => {
            if (item.type === "jpg" || item.originalFilename?.toLowerCase()?.endsWith(".jpg")) {
                acc.photoFiles.push(item);
            } else if (item.type === "video") {
                acc.videoFiles.push(item);
            } else {
                acc.otherFiles.push(item);
            }

            return acc;
        },
        { photoFiles: [], videoFiles: [], otherFiles: [] }
    );

    const [downloadProgress, setDownloadProgress] = useState({
        ...downloadProgressInitialState,
    });
    const [bundleDownloadLink, setBundleDownloadLink] = useState("");

    const downloadSelecteds = async (filesType) => {
        setLoadingLink((a) => [...a, filesType]);
        const withToken = token ? `/${token}` : "";

        if (token) {
            return api
                .get(`/v1/customer/jobs/${job.id}/files/${filesType}/download-link${withToken}`)
                .then((res) => res.data)
                .then(async ({ record }) => {
                    window.location.href = record;
                    return null;
                })
                .catch((e) => {
                    console.log(e);
                    toast.error(`Erro ao fazer o download, ${e}`);
                })
                .finally(() => {
                    setLoadingLink((a) => a.filter((i) => i !== filesType));
                });
        }

        api.get(`/v3/customer/jobs/${job.id}/files/${filesType}/download-link${withToken}`)
            .then((res) => res.data)
            .then(async ({ record: files, bundle }) => {
                if (job.type === "business") {
                    window.location.href = bundle;
                    return null;
                }

                const zip = JSZip();
                setBundleDownloadLink(bundle);

                const { images, videos, others } = files.reduce(
                    (acc, file) => {
                        if (file.key.toLowerCase().endsWith(".jpg")) {
                            acc.images += 1;
                        } else if (file.key.toLowerCase().endsWith(".mp4")) {
                            acc.videos += 1;
                        } else {
                            acc.others += 1;
                        }

                        return acc;
                    },
                    { images: 0, videos: 0, others: 0 }
                );

                setDownloadProgress((p) => ({ ...p, imagesTotal: images, videosTotal: videos, othersTotal: others }));

                const promises = files.map(async (currentFile, index) => {
                    return fetch(currentFile.downloadUrl)
                        .then((res) => res.blob())
                        .then((content) => {
                            zip.file(`${index}_${findFilenameOnUrl(currentFile.downloadUrl)}`, content);

                            if (currentFile.key.toLowerCase().endsWith(".jpg")) {
                                setDownloadProgress((p) => ({ ...p, imagesDownloaded: p.imagesDownloaded + 1 }));
                            } else if (currentFile.key.toLowerCase().endsWith(".mp4")) {
                                setDownloadProgress((p) => ({ ...p, videosDownloaded: p.videosDownloaded + 1 }));
                            } else {
                                setDownloadProgress((p) => ({ ...p, othersDownloaded: p.othersDownloaded + 1 }));
                            }
                        });
                });

                return Promise.all(promises).then(() => zip);
            })
            .then((zip) => {
                if (!zip) {
                    return null;
                }

                zip.generateAsync({ type: "blob" }).then((content) => {
                    saveAs(content, `${job.id}.zip`);

                    mainApi.log({
                        data: {
                            token,
                            type: "success",
                            action: "customerDownloadBundle",
                            resource: "downloadCustomerBundle",
                            jobId: job.id,
                            when: new Date(),
                        },
                    });
                });
            })
            .catch((e) => {
                console.log(e);

                mainApi.log({
                    data: {
                        token,
                        type: "error",
                        action: "customerDownloadBundle",
                        resource: "downloadCustomerBundle",
                        jobId: job.id,
                        when: new Date(),
                        error: e.message,
                        errorStack: e.stack,
                    },
                });

                if (bundleDownloadLink) {
                    window.open(bundleDownloadLink);
                    return;
                }

                toast.error(`Erro ao fazer o download, ${e}`);
            })
            .finally(() => {
                setLoadingLink((a) => a.filter((i) => i !== filesType));
                setDownloadProgress({ ...downloadProgressInitialState });
                setBundleDownloadLink("");
            });
    };

    const downloadBundleWithLink = () => {
        window.open(bundleDownloadLink);

        mainApi.log({
            data: {
                token,
                type: "error",
                action: "directLinkBundleDownload",
                resource: "downloadCustomerBundle",
                jobId: job.id,
                when: new Date(),
            },
        });
    };

    const openIntegrationModal = () => {
        setShowIntegrationModal(true);
    };

    const goToVideoSlideshow = () => {
        if (!job?.videoProductSummary?.allowed) {
            renderDialog(
                <>
                    <Title>Você não pode mais gerar vídeos</Title>
                    <p>Você já consumiu seus créditos e, por isso, não pode gerar vídeos fotográficos para esse job.</p>
                </>,
                "Sair",
                () => {}
            );
        } else {
            router.push(`/gallery/video-product/${job.id}`);
        }
    };

    const getFeedBackItens = async () => {
        return FeedbackItem.list({ filters: { respondent: "cliente", status: "ativo" } })
            .then((res) => setFeedbackItens(res))
            .catch((err) => {
                toast.error(
                    `Não foi possível recuperar os itens do feedback: ${err?.response?.data?.msg || err.message}`
                );
            });
    };

    const getFeedbackJob = async () => {
        return FeedbackJob.list({ filters: { jobId: job?.id } })
            .then((res) => setFeedbackJobList(res))
            .catch((err) => {
                toast.error(
                    `Não foi possível recuperar a listagem dos feedbacks: ${err?.response?.data?.msg || err.message}`
                );
            });
    };

    useEffect(() => {
        if (job.id && !token) {
            getFeedBackItens();
            getFeedbackJob();
        }
    }, [job]);

    useEffect(() => {
        if (job?.id && job?.organization?.id) {
            getRequest(job.id, job.organization.id);
        }
    }, [job?.id, job?.organization?.id]);

    const addVideoFallback = (event) => {
        event.currentTarget.src = "/images/video-fallback.jpg";
    };

    return (
        <>
            <ExecuteIntegrationModal
                show={showIntegrationModal}
                integrations={job?.integrations ?? []}
                closeFn={() => setShowIntegrationModal(false)}
                job={job}
                files={photoFiles}
            />

            <RequestCreationModal
                isOpen={requestModalOpen}
                onClose={() => setRequestModalOpen(false)}
                customerId={account?.id}
                job={{
                    jobId: job.id,
                    jobDate: job?.scheduling?.scheduleDate,
                    customerCode: job?.customerCode,
                    address: job?.address.fullAddress,
                }}
                afterPost={() => {
                    getRequest(job.id);
                    setRequestModalOpen(false);
                }}
            />

            <EvaluationModal
                isOpen={openFeedbackModal}
                onClose={() => setOpenFeedBackModal(false)}
                feedbackItens={feedbackItens}
                feedbackJobList={feedbackJobList}
                numberOfVideos={videoFiles?.length}
                jobDetails={job}
                afterRating={() => {
                    getFeedbackJob();
                }}
            />

            <DashboardHeader>
                {!token && account.grantIntegrationExecution && job?.integrations?.length ? (
                    <SubmenuItem onClick={openIntegrationModal}>Integrações</SubmenuItem>
                ) : (
                    ""
                )}
                {job?.type === "business" && account?.grantGalleryAccess && job.videoProductSummary && job.videoProductAllowed === "ativo" ? (
                    <SubmenuItem onClick={goToVideoSlideshow}>Vídeo fotográfico</SubmenuItem>
                ) : (
                    ""
                )}
                <SubmenuItem onClick={() => downloadSelecteds("bundle")}>
                    {loadingLink.includes("bundle") ? <Spinner /> : "Download de todos os arquivos"}
                </SubmenuItem>

                {!hideControls ? (
                    <SubmenuItem onClick={() => setRequestModalOpen(true)} block>
                        Nova solicitação
                    </SubmenuItem>
                ) : (
                    ""
                )}
            </DashboardHeader>

            <Container style={{ paddingBottom: "20px" }} cover={cover || "/images/gallery-placeholder-big.jpg"}>
                <div className="container">
                    <div className="cover">
                        {!hideControls && job.type === "business" ? (
                            <div className="cover-buttons">
                                <ShareGallery jobId={job.id} galleryShareToken={job.galleryShortToken} />
                            </div>
                        ) : (
                            ""
                        )}

                        <p style={{ color: "#fff" }}>
                            {getAddressFormatted({
                                addressParts: {
                                    address: job?.address?.street,
                                    number: job?.address?.number,
                                    complement: job?.address?.complement,
                                    cityName: job?.address?.city?.name,
                                    stateAbbr: job?.address?.state?.uf,
                                },
                            })}
                            <span>{(job && moment(job.scheduling.scheduleDate).format("D/MM/YYYY")) || ""}</span>
                        </p>
                    </div>

                    {!hideControls && (job?.publishingStatus?.customerComment || job?.execution?.observation) ? (
                        <div>
                            <div className="gallery" style={{ margin: "1rem 0" }}>
                                <div className="gallery-header">
                                    <BsChatSquareTextFill size={22} color="#FE8418" />
                                    <p className="f-b">Mensagens </p>
                                </div>
                            </div>
                            {job?.publishingStatus.customerComment ? (
                                <Panel>
                                    <MessageContent>
                                        <div className="message-header">
                                            <img src="/images/fotop-circulo.svg" alt="logo fotop" />
                                            <strong>Comentário da equipe de edição</strong>
                                        </div>
                                        <p>{job?.publishingStatus.customerComment}</p>
                                    </MessageContent>
                                </Panel>
                            ) : (
                                ""
                            )}
                            {job?.execution?.observation ? (
                                <Panel style={{ marginTop: "1.5rem" }}>
                                    <MessageContent>
                                        <div className="message-header">
                                            <img src="/images/fotop-circulo.svg" alt="logo fotop" />
                                            <strong>Comentário do fotógrafo</strong>
                                        </div>
                                        <p>{job?.execution?.observation}</p>
                                    </MessageContent>
                                </Panel>
                            ) : (
                                ""
                            )}
                        </div>
                    ) : (
                        ""
                    )}

                    {/* <div className="info">
                        <div></div>
                        <div>
                            <DownloadButton
                                disabled={loadingLink.includes("bundle")}
                                onClick={() => downloadSelecteds("bundle")}
                            >
                                {loadingLink.includes("bundle") ? <Spinner /> : "Download de todos os arquivos"}
                            </DownloadButton>
                        </div>
                    </div> */}

                    {requests && requests.length && !hideControls ? (
                        <div className="col-12 mt-4">
                            <header>
                                <Title featured level="2">
                                    <BsTable /> Informações da solicitação
                                </Title>
                            </header>
                            <Panel>
                                <DataTable records={requests} loading={loadRequest}>
                                    <DataTable.Computed
                                        style={{ width: "25px" }}
                                        render={(row) => <Status row={row} />}
                                    />
                                    <DataTable.Link field="id" label="ID" link={(id) => `/requests/${id}`} />
                                    <DataTable.Text field="reason" label="Motivo" />
                                    <DataTable.Text field="observation" label="Comentário" />
                                    <DataTable.Text field="user.nome" label="Solicitante" />
                                    <DataTable.DateFromISO8601 field="createdAt" label="Data" to="dd/MM/yyyy" />
                                    <DataTable.Computed
                                        style={{ width: "25px" }}
                                        render={(row) => <Notification requestId={row.id} />}
                                    />
                                </DataTable>
                            </Panel>
                        </div>
                    ) : (
                        ""
                    )}

                    <div id="gallery" className="gallery mt-2">
                        {photoFiles?.length ? (
                            <>
                                <div className="gallery-header">
                                    <div className="gallery-title">
                                        <BsImageFill size={22} color="#FE8418" />
                                        <p className="f-b">
                                            Galeria de fotos{" "}
                                            {photoFiles.length ? <Badge small>{photoFiles.length}</Badge> : ""}
                                        </p>
                                        <div className="text-right" style={{ marginRight: "0.2rem" }}>
                                            {job.hasImageGallery ? (
                                                <DownloadButton onClick={() => downloadSelecteds("gallery")}>
                                                    {loadingLink.includes("gallery") ? <Spinner /> : "Download"}
                                                </DownloadButton>
                                                ) : (
                                                    ""
                                                    )}
                                        </div>
                                    </div>

                                    {
                                      !hideControls ? (
                                          <div className="rate-content">
                                              <div className="rate">
                                                  <button
                                                      onClick={() => setOpenFeedBackModal(true)}
                                                      style={{ background: "transparent", cursor: "pointer" }}
                                                      >
                                                      <Rating
                                                          name="Qualidade das fotos"
                                                          onChange={() => {}}
                                                          showTippy={false}
                                                          value={
                                                        feedbackJobList.find(
                                                            (item) => item?.feedbackName === "Qualidade das fotos"
                                                            )?.grade
                                                    }
                                                      />
                                                  </button>
                                              </div>
                                          </div>
                                      ) : ''
                                    }

                                </div>
                                <Masonry
                                    breakpointCols={{ default: 3, 1100: 3, 700: 2 }}
                                    className="masonry-grid"
                                    columnClassName="masonry-grid_column"
                                >
                                    {photoFiles.map((photo) => {
                                        return (
                                            <div key={photo.id} className="photo" onClick={() => lightbox(photo.id)}>
                                                <img src={photo.thumb} />
                                                <div className="actions">
                                                    <CircleButton
                                                        className={`${loadingLink.includes(photo.id) ? "animate" : ""}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            download(photo.id);
                                                        }}
                                                    >
                                                        {loadingLink.includes(photo.id) ? (
                                                            <FaSpinner />
                                                        ) : (
                                                            <FTOPDownload />
                                                        )}
                                                    </CircleButton>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Masonry>
                            </>
                        ) : (
                            ""
                        )}
                        {videoFiles?.length ? (
                            <>
                                <div className="gallery-header">
                                    <div className="gallery-title">
                                        <RiVideoFill size={22} color="#FE8418" />
                                        <p className="f-b">
                                            Galeria de videos{" "}
                                            {videoFiles.length ? <Badge small>{videoFiles.length}</Badge> : ""}
                                        </p>
                                    </div>
                                    {
                                        !hideControls ? (
                                            <div className="rate-content">
                                                <div className="rate">
                                                    <button
                                                        onClick={() => setOpenFeedBackModal(true)}
                                                        style={{ background: "transparent", cursor: "pointer" }}
                                                        >
                                                        <Rating
                                                            name="Qualidade do vídeo"
                                                            onChange={() => {}}
                                                            showTippy={false}
                                                            value={
                                                        feedbackJobList.find(
                                                            (item) => item?.feedbackName === "Qualidade do vídeo"
                                                            )?.grade
                                                    }
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : ''
                                    }

                                </div>
                                <Masonry
                                    style={{ width: "100%" }}
                                    breakpointCols={{ default: 3, 1100: 3, 700: 2 }}
                                    className="masonry-grid"
                                    columnClassName="masonry-grid_column"
                                >
                                    {videoFiles.map((video) => (
                                        <div key={video.id} className="photo">
                                            <img src={video.thumb} onError={addVideoFallback} />
                                            <div className="actions">
                                                <CircleButton
                                                    className={`${loadingLink.includes(video.id) ? "animate" : ""}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        download(video.id);
                                                    }}
                                                >
                                                    {loadingLink.includes(video.id) ? <FaSpinner /> : <FTOPDownload />}
                                                </CircleButton>
                                            </div>
                                        </div>
                                    ))}
                                </Masonry>
                            </>
                        ) : (
                            ""
                        )}

                        {otherFiles?.length ? (
                            <>
                                <div className="gallery-header">
                                    <div className="gallery-title">
                                        <BsFileEarmark size={22} color="#FE8418" />
                                        <p className="f-b">Outros arquivos: </p>
                                    </div>
                                </div>
                                <ul>
                                    {otherFiles?.map((item) => (
                                        <li key={item.id} style={otherFilesItemStyle}>
                                            <a onClick={() => download(item.id)} style={{ cursor: "pointer", color: "#0d6efd" }}>
                                                {item.originalFilename} {loadingLink.includes(item.id) ? <Spinner /> : ""}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ) : (
                            ""
                        )}

                        {job.tourUrl ? (
                            <>
                                <div className="gallery-header">
                                    <div className="gallery-title">
                                        <BsLink45Deg size={22} color="#FE8418" />
                                        <p className="f-b">Tour: </p>
                                    </div>
                                </div>

                                <ul>
                                    {job.tourUrl.includes(";") ? (
                                        <>
                                            {job.tourUrl.split(";").map((url) => (
                                                <li key={url} style={otherFilesItemStyle}>
                                                    <a href={url} rel="noopener nofollow noreferrer" target="_blank">
                                                        {url}
                                                    </a>
                                                </li>
                                            ))}
                                        </>
                                    ) : (
                                        <li style={otherFilesItemStyle}>
                                            <a href={job.tourUrl} rel="noopener nofollow noreferrer" target="_blank">
                                                {job.tourUrl}
                                            </a>
                                        </li>
                                    )}
                                </ul>
                            </>
                        ) : (
                            ""
                        )}
                    </div>
                </div>

                <DownloadDrawer
                    bundle={bundleDownloadLink ? downloadBundleWithLink : null}
                    open={downloadProgress.imagesTotal + downloadProgress.videosTotal + downloadProgress.othersTotal}
                    downloadProgress={downloadProgress}
                />
            </Container>
            <Lightbox
                photos={photos}
                currentPhotoId={currentPhotoId}
                setCurrentPhotoId={setCurrentPhotoId}
                isVisible={showLightbox}
                setShowLightbox={setShowLightbox}
            />
        </>
    );
};

export default Gallery;
